import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import {
  filterLpNavSections,
  getLpSectionFromElement,
  getNextLpNavSection,
  scrollToLpSection,
} from '@theme/lp-scroll-utils';

class LpStickyNav extends HTMLElement {
  #hideObserver = null;
  #hideAnchorVisible = false;
  #onBreakpointChange = () => this.#syncVisibility();
  #onScroll = () => this.#syncVisibility();
  #onContinue = () => this.#scrollToNextSection();
  #onSkip = () => this.#scrollToSkipTarget();

  connectedCallback() {
    this.continueButton = this.querySelector('[data-ref="continue-button"]');
    this.skipButton = this.querySelector('[data-ref="skip-button"]');

    this.continueButton?.addEventListener('click', this.#onContinue);
    this.skipButton?.addEventListener('click', this.#onSkip);
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#setupHideObserver();
    this.#syncVisibility();
  }

  disconnectedCallback() {
    this.continueButton?.removeEventListener('click', this.#onContinue);
    this.skipButton?.removeEventListener('click', this.#onSkip);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    window.removeEventListener('scroll', this.#onScroll);
    this.#hideObserver?.disconnect();
    this.#hideObserver = null;
  }

  #shouldShowOnCurrentBreakpoint() {
    const mode = this.dataset.showOn || 'mobile_only';

    if (mode === 'desktop_only') return !isMobileBreakpoint();
    if (mode === 'mobile_only') return isMobileBreakpoint();

    return true;
  }

  #getActiveSection() {
    const sections = this.#getNavigableSections();
    if (!sections.length) return null;

    const currentIndex = this.#getCurrentSectionIndex(sections);
    return sections[currentIndex] ?? null;
  }

  #syncVisibility() {
    if (!this.#shouldShowOnCurrentBreakpoint() || this.#hideAnchorVisible) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
  }

  #getNavigableSections() {
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    const footerGroup = document.querySelector('.shopify-section-group-footer-group');

    const sections = [...document.querySelectorAll('.shopify-section')].filter((section) => {
      if (!(section instanceof HTMLElement)) return false;
      if (headerGroup?.contains(section)) return false;
      if (footerGroup?.contains(section)) return false;
      if (section.classList.contains('shopify-section-group')) return false;
      return this.#isSectionVisible(section);
    });

    return filterLpNavSections(sections);
  }

  #isSectionVisible(section) {
    const style = getComputedStyle(section);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return section.getBoundingClientRect().height > 0;
  }

  #getCurrentSectionIndex(sections) {
    const referenceY = window.scrollY + window.innerHeight * 0.35;
    let index = 0;

    for (let i = 0; i < sections.length; i++) {
      if (sections[i].offsetTop <= referenceY) index = i;
    }

    return index;
  }

  #scrollToSection(section) {
    scrollToLpSection(section);
  }

  #scrollToNextSection() {
    const sections = this.#getNavigableSections();
    if (!sections.length) return;

    const currentIndex = this.#getCurrentSectionIndex(sections);
    const nextSection = getNextLpNavSection(sections, currentIndex);

    if (nextSection) this.#scrollToSection(nextSection);
  }

  #scrollToSkipTarget() {
    const anchorId = this.dataset.skipAnchor?.trim();
    if (!anchorId) return;

    const target = document.getElementById(anchorId);
    if (!target) return;

    const section = getLpSectionFromElement(target);
    if (section) scrollToLpSection(section);
  }

  #setupHideObserver() {
    const anchorId = this.dataset.hideAnchor?.trim();
    if (!anchorId) return;

    const target = document.getElementById(anchorId);
    if (!target) return;

    const observed = target.closest('.shopify-section') ?? target;

    this.#hideObserver?.disconnect();

    this.#hideObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const sectionTop = observed.getBoundingClientRect().top;
          this.#hideAnchorVisible = entry.isIntersecting && sectionTop < window.innerHeight * 0.55;
        }

        this.#syncVisibility();
      },
      { threshold: [0, 0.15, 0.35, 0.5] },
    );

    this.#hideObserver.observe(observed);
  }
}

if (!customElements.get('lp-sticky-nav')) {
  customElements.define('lp-sticky-nav', LpStickyNav);
}
